import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CY = '#90E0EF';
const SYM = { GBP: '£', EUR: '€', USD: '$', PLN: 'zł' };
function money(minor, cur = 'GBP') {
  const v = (Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur === 'PLN' ? `${v} ${SYM[cur]}` : `${SYM[cur] || ''}${v}`;
}

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  const [sale, setSale] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [tries, setTries] = useState(0);

  // Poll the status endpoint (which itself verifies with Stripe) — never
  // trust the redirect alone as proof of payment.
  useEffect(() => {
    if (!code) return;
    let stop = false;
    let n = 0;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/public/sales/${code}/status`);
        if (stop) return;
        setSale(data);
        if (data.status === 'paid') { setConfirmed(true); return; }
      } catch { /* keep trying */ }
      n += 1; setTries(n);
      if (!stop && n < 12) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [code]);

  return (
    <div className="min-h-[100svh] bg-[#050505] text-[#f0f0f0] relative overflow-hidden" data-testid="payment-success-page">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(#90E0EF 1px, transparent 1px), linear-gradient(90deg, #90E0EF 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
      <div className="relative z-10 max-w-md mx-auto px-6 py-16 text-center">
        <div className="flex items-center justify-center gap-2.5 mb-10">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: CY, boxShadow: `0 0 8px ${CY}` }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CY }}>{sale?.seller_label || 'The Relative Gallery'}</span>
        </div>

        {confirmed ? (
          <>
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ border: `1.5px solid ${CY}`, background: 'rgba(144,224,239,0.1)' }}>
              <Check className="w-7 h-7" style={{ color: CY }} />
            </div>
            <h1 className="font-outfit font-bold text-white tracking-tight" style={{ fontSize: '2rem' }}>Payment Confirmed</h1>
            {sale && (
              <div className="mt-8 border-t border-b border-[#1a1a1a] py-6">
                <p className="text-lg font-outfit text-white">{sale.item_title}{sale.edition ? ` · ${sale.edition}` : ''}</p>
                {sale.artist_name && <p className="text-xs font-mono text-[#888] mt-1">{sale.artist_name}</p>}
                <p className="font-outfit font-bold text-white mt-4" style={{ fontSize: '2.2rem', lineHeight: 1 }}>{money(sale.amount_minor, sale.currency)}</p>
                {sale.buyer_name && <p className="text-xs font-mono text-[#666] mt-3">{sale.buyer_name}</p>}
              </div>
            )}
            <p className="text-xs font-mono text-[#888] mt-6">Thank you. The gallery will be in touch shortly.</p>
          </>
        ) : (
          <>
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-6" style={{ border: '1.5px solid #333' }}>
              {tries < 12 ? <Loader2 className="w-6 h-6 animate-spin" style={{ color: CY }} /> : <Clock className="w-6 h-6 text-[#888]" />}
            </div>
            <h1 className="font-outfit font-semibold text-white tracking-tight" style={{ fontSize: '1.6rem' }}>
              {tries < 12 ? 'Confirming your payment…' : 'Almost there'}
            </h1>
            <p className="text-xs font-mono text-[#888] mt-4 leading-relaxed">
              {tries < 12
                ? 'We are verifying the payment with Stripe. This takes a few seconds.'
                : 'Your payment is processing. You can safely close this page — the gallery will confirm your acquisition.'}
            </p>
          </>
        )}

        <div className="mt-10">
          <Link to="/" className="text-[11px] font-mono text-[#666] hover:text-white transition-colors">← The Relative Gallery</Link>
        </div>
      </div>
    </div>
  );
}
